import { ChevronRight } from 'lucide-react'
import React, { useState, useEffect } from 'react'

interface SimpleCalendarProps {
    onDateSelect: (date: string) => void
    value?: string //? dd/mm/yyyy
}

const SimpleCalendar: React.FC<SimpleCalendarProps> = ({ onDateSelect, value }) => {
    const parseDate = (dateStr: string): Date | null => {
        const parts = dateStr.split('/')
        if (parts.length === 3) {
            const [day, month, year] = parts.map(Number)
            return new Date(year, month - 1, day) // Ajusta o mês (0-based)
        }
        return null
    }

    const initialDate = value ? parseDate(value) : new Date()
    const [currentDate, setCurrentDate] = useState(initialDate || new Date())
    const [days, setDays] = useState<Date[]>([])
    const [emptySlots, setEmptySlots] = useState<number>(0)
    const [selectedDate, setSelectedDate] = useState<string>(value ? value.split('/').reverse().join('-') : '')

    // Atualiza o calendário sempre que `value` mudar e for válido
    useEffect(() => {
        if (value && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            const parsedDate = parseDate(value)
            if (parsedDate) {
                setSelectedDate(value.split('/').reverse().join('-')) // Converte para 'yyyy-mm-dd'
                setCurrentDate(parsedDate) // Atualiza o mês/ano do calendário
            }
        }
    }, [value])

    useEffect(() => {
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()

        // Define o primeiro dia do mês e quantos "slots vazios" (dias da semana anteriores)
        const firstDayOfMonth = new Date(year, month, 1)
        const dayOfWeek = firstDayOfMonth.getDay() // 0 (Domingo) a 6 (Sábado)
        setEmptySlots(dayOfWeek)

        // Obter o último dia do mês
        const lastDayOfMonth = new Date(year, month + 1, 0)
        const numDays = lastDayOfMonth.getDate()
        const daysArray: Date[] = []
        for (let day = 1; day <= numDays; day++) {
            daysArray.push(new Date(year, month, day))
        }
        setDays(daysArray)
    }, [currentDate])

    const formatDate = (date: Date) => date.toISOString().split('T')[0]

    const handleDateClick = (date: Date) => {
        const formatted = formatDate(date)
        setSelectedDate(formatted)
        onDateSelect(formatted)
    }

    const today = formatDate(new Date())

    return (
        <div className=''>
            <div className='flex justify-between items-center mb-2'>
                <div className='font-semibold'>
                    {currentDate.toLocaleString('default', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}{' '}
                    {currentDate.getFullYear()}
                </div>
                <div className='flex items-center gap-1'>
                    <button
                        type='button'
                        className='text-sm text-gray-500 p-2 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full'
                        onClick={() =>
                            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))
                        }
                    >
                        <ChevronRight className='w-4 h-4 rotate-180' />
                    </button>
                    <button
                        type='button'
                        className='text-sm text-gray-500 p-2 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full'
                        onClick={() =>
                            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))
                        }
                    >
                        <ChevronRight className='w-4 h-4' />
                    </button>
                </div>
            </div>
            <div className='grid grid-cols-7 gap-2'>
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                    <div key={d} className='text-xs text-center'>
                        {d}
                    </div>
                ))}

                {/* Renderiza os espaços vazios para alinhar o primeiro dia */}
                {Array.from({ length: emptySlots }).map((_, index) => (
                    <div key={`empty-${index}`} />
                ))}

                {days.map((day, index) => {
                    const formatted = formatDate(day)
                    const isToday = formatted === today
                    const isSelected = formatted === selectedDate

                    return (
                        <button
                            key={index}
                            onClick={() => handleDateClick(day)}
                            className={`text-sm p-1 rounded-lg focus:outline-none 
                ${isToday ? 'bg-brand-100 dark:bg-brand-500/[0.12]' : ''} 
                ${isSelected ? 'bg-brand-500 text-white' : 'hover:bg-brand-50 dark:hover:bg-brand-500/[0.12]'}`}
                        >
                            {day.getDate()}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default SimpleCalendar
